import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@/components';
import {
  useGetAreaCodes,
  useGetCardTypes,
  useGetCities,
  useGetRegions,
} from '@xhype/card-app-sdk';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useGetCardHolderDataWithId,
  useGetUserData,
} from '@package/smart-wallet';
import { useUpdateUserCardHolder } from '@package/smart-wallet/hooks';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

/* Schemas */
export const CardHolderFullDataFormSchema = z.object({
  country: z.string().min(1, { message: 'Country is required' }),
  cardTypeId: z.string().min(1, { message: 'Card Type is required' }),
  firstName: z.string().min(1, { message: 'First Name is required' }),
  lastName: z.string().min(1, { message: 'Last Name is required' }),
  areaCode: z.string().min(1, { message: 'Area Code is required' }),
  mobile: z
    .string()
    .min(1, { message: 'Mobile number is required' })
    .refine((value) => /^\d+$/.test(value), {
      message: 'Invalid mobile number',
    }),
  address: z.string().min(1, { message: 'Address is required' }),
  birthday: z
    .string()
    .min(1, { message: 'Birthday is required' })
    .refine((value) => !isNaN(Date.parse(value)), {
      message: 'Invalid date format',
    }),
  town: z.string().min(1, { message: 'Town is required' }),
  postCode: z.string().min(1, { message: 'Post Code is required' }),
});

/* Types */
export type CardHolderFullDataFormType = z.infer<
  typeof CardHolderFullDataFormSchema
>;

/* Internal components */
const CardHolderFullDataForm = () => {
  const [holderId, setHolderId] = useState<number | undefined>(undefined);
  const form = useForm<CardHolderFullDataFormType>({
    resolver: zodResolver(CardHolderFullDataFormSchema),
  });

  /* Mutations */
  const { updateCardHolderData, isLoading } = useUpdateUserCardHolder({});

  /* Queries */
  const { userInfo } = useGetUserData();
  const { cardHolderInfo } = useGetCardHolderDataWithId({ holderId });

  const { data: regions } = useGetRegions({
    onError: (error: Error) => {
      console.error(`Get Regions: Axios error occurred - ${error.message}`);
    },
  });

  const { data: cities } = useGetCities({
    regionCode: form.getValues('country'),
    enabled: form.getValues('country') !== '',
    onError: (error: Error) => {
      console.error(`Get Cities: Axios error occurred - ${error.message}`);
    },
  });

  const { data: areaCodes } = useGetAreaCodes({
    onError: (error: Error) => {
      console.error(`Get Area Codes: Axios error occurred - ${error.message}`);
    },
  });

  const { data: cardTypes } = useGetCardTypes({
    onError: (error: Error) => {
      console.error(`Get Card Type: Axios error occurred - ${error.message}`);
    },
  });

  /* Callbacks */
  const onSubmit = useCallback(
    async (data: CardHolderFullDataFormType) => {
      try {
        if (!holderId) {
          console.error('Holder ID is not set');
          return;
        }
        await updateCardHolderData({
          ...data,
          holderId,
        });
      } catch (error) {
        console.error('Error submitting form:', error);
      }
    },
    [holderId, updateCardHolderData],
  );

  /* Memos */
  const holderIds = useMemo(
    () => userInfo?.cardHolder.map((card) => card.id) || [],
    [userInfo],
  );

  const countries = useMemo(
    () => regions?.map((region) => region.code) || [],
    [regions],
  );

  const mobileAreaCodes = useMemo(
    () => areaCodes?.map((areaCode) => areaCode.code) || [],
    [areaCodes],
  );

  const cardTypesId = useMemo(
    () => cardTypes?.map((cardType) => cardType.cardTypeId.toString()) || [],
    [cardTypes],
  );

  const towns = useMemo(() => cities?.map((city) => city.code) || [], [cities]);

  const externalFields = useMemo(() => {
    return [{ name: 'holderId', label: 'Card Holder Id', options: holderIds }];
  }, [holderIds]);

  const formFields = useMemo(() => {
    return [
      {
        name: 'country',
        label: 'Country',
        type: 'dropdown',
        options: countries,
      },
      {
        name: 'cardTypeId',
        label: 'Card Type',
        type: 'dropdown',
        options: cardTypesId,
      },
      { name: 'firstName', label: 'First Name', type: 'input' },
      { name: 'lastName', label: 'Last Name', type: 'input' },
      { name: 'birthday', label: 'Birthday (YYYY-MM-DD)', type: 'input' },
      {
        name: 'areaCode',
        label: 'Area Code',
        type: 'dropdown',
        options: mobileAreaCodes,
      },
      { name: 'mobile', label: 'Mobile', type: 'input' },
      {
        name: 'town',
        label: 'Town',
        type: 'dropdown',
        options: towns,
      },
      { name: 'address', label: 'Address', type: 'input' },
      { name: 'postCode', label: 'Post Code', type: 'input' },
    ];
  }, [cardTypesId, mobileAreaCodes, towns, countries]);

  /* Effects */
  useEffect(() => {
    if (cardHolderInfo) {
      form.reset({
        country: cardHolderInfo.country,
        cardTypeId: cardHolderInfo.cardTypeId.toString(),
        firstName: cardHolderInfo.firstName,
        lastName: cardHolderInfo.lastName,
        areaCode: cardHolderInfo.areaCode,
        mobile: cardHolderInfo.mobile,
        address: cardHolderInfo.address,
        birthday: cardHolderInfo.birthday,
        town: cardHolderInfo.town,
        postCode: cardHolderInfo.postCode,
      });
    }
  }, [cardHolderInfo, form]);

  /* Render */
  return (
    <section className='w-full flex flex-col gap-4'>
      <section className='w-full flex flex-col gap-2'>
        {externalFields.map(({ label, options }) => (
          <div className='flex flex-col gap-2'>
            <span className='text-sm font-medium'>{label}</span>
            <select
              className='border rounded p-2'
              onChange={(e) =>
                setHolderId(e.target.value ? Number(e.target.value) : undefined)
              }
            >
              <option value=''>Select {label}</option>
              {options?.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        ))}
      </section>
      {!!holderId && (
        <Form {...form}>
          <form
            className='w-full flex flex-col gap-2'
            onSubmit={form.handleSubmit(onSubmit)}
          >
            {formFields.map(({ name, label, type, options }) => (
              <FormField
                key={name}
                control={form.control}
                name={name as keyof CardHolderFullDataFormType}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{label}</FormLabel>
                    <FormControl>
                      {type === 'dropdown' ? (
                        <select {...field} className='border rounded p-2'>
                          <option value=''>Select {label}</option>
                          {options?.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <Input {...field} placeholder={label} />
                      )}
                    </FormControl>
                    <FormMessage className='text-start' />
                  </FormItem>
                )}
              />
            ))}
            <Button
              className='w-full cursor-pointer uppercase'
              type='submit'
              disabled={form.formState.isSubmitting || isLoading}
            >
              {form.formState.isSubmitting || isLoading
                ? 'Updating...'
                : 'Update'}
            </Button>
          </form>
        </Form>
      )}
    </section>
  );
};

/* Main component */
export const UpdateCardHolder = () => {
  return (
    <div className='h-full w-full flex flex-col items-center justify-center'>
      <section className='w-2/3 flex flex-col items-center justify-center gap-4'>
        <CardHolderFullDataForm />
      </section>
    </div>
  );
};
